// @flow
import websocket from 'websocket';
import { Socket } from 'phoenix';

import type { RPS_ACTION_TYPE } from '../types/socket';
import { DEFAULT_SOCKET_ROLE } from '../settings';
import * as types from '../types/socket';
import * as actions from '../actions/socket';

import { Trips, School } from '../api';


type DISPATCH_TYPE = Function;


// ///////////// SOCKET MODULE INTERNAL STATE ///////////////

let socket;
const channels = {};

// //////////////////////////////////////////////////////////

type GetTokenProps = {
  resource: (any) => Promise<any>,
  channelDetail: string,
  role: string,
  token?: string,
  resolve: Function,
};

const GetToken = ({
  resource,
  channelDetail,
  role,
  token,
  resolve,
}: GetTokenProps) => {
  resource({
    token,
    id: channelDetail,
    data: {
      role,
    },
  })
    .then(data => resolve(data))
    .catch(error => console.error(error));
};

const attachChannelEvents = ({
  actionEvent,
  channel,
  channelId,
  userId,
  dispatch,
}) => {
  // Attatch the execute action event
  channel.on(actionEvent, dispatch);

  // Listen for default lifecycle events
  channel.join()
    .receive('ok', () => dispatch(actions.completeJoin(userId, channelId)))
    .receive('error', (data) => {
      dispatch(actions.failJoin(userId, channelId, data));
      console.error(data);
    })
    .receive('timeout', () => actions.doTimeout(channelId));
};

const createPhoenixSocketMiddleware = (
  actionEvent: string = 'execute:action',
) => ({
  dispatch,
}: { dispatch: DISPATCH_TYPE }) => (
  next: DISPATCH_TYPE,
) => (
  action: RPS_ACTION_TYPE,
) => {
  switch (action.type) {
    // CLIENT CLOSED SOCKET
    case types.RPS_CLOSED: {
      if (typeof socket !== 'undefined' /* || check if its closed */) {
        socket.disconnect(() => next(action), 1000, 'Connection closed by client');

        // Stop middleware chain
        return {};
      }

      break;
    }
    case types.RPS_OPEN_STARTED: {
      const {
        payload: {
          host,
          userId,
          token,
          defaultRooms,
          role,
        },
      } = action;

      // Clear old callbacks
      if (typeof socket !== 'undefined') {
        socket.disconnect();
      }

      // Create new socket reference
      socket = new Socket(
        host,
        {
          transport: websocket.w3cwebsocket,
          params: { userId, token, role },
        },
      );

      socket.connect();
      socket.onOpen(() => {
        dispatch(actions.completeOpen(host, userId));

        // Join to the default channels
        defaultRooms.forEach(
          channelId => dispatch(
            actions.join(
              userId,
              token,
              DEFAULT_SOCKET_ROLE,
              channelId,
            ),
          ),
        );
      });
      socket.onError(data => dispatch(actions.doError(data)));
      socket.onClose(() => dispatch(actions.disconnect(host)));

      break;
    }
    case types.RPS_JOIN_STARTED: {
      if (typeof socket !== 'undefined' /* && check if its open */) {
        const {
          payload: {
            userId,
            channelId,
            token,
            role,
          },
        } = action;

        // If there is a channel for channelId key
        if (typeof channels[channelId] !== 'undefined') {
          // Leave the channel
          channels[channelId].leave();
        }

        const [channelName, channelDetail] = channelId.split(':');

        switch (channelName) {
          case 'school': {
            GetToken({
              resource: School.custom.getRoomToken,
              channelDetail,
              role: DEFAULT_SOCKET_ROLE,
              token,
              resolve: (data) => {
                // Create and store the new channel
                const channel = socket.channel(channelId, { id: userId, token: data.token, role });
                channels[channelId] = channel;

                // Attatch the default events to the joineed channel
                attachChannelEvents({
                  actionEvent,
                  channel,
                  channelId,
                  userId,
                  dispatch,
                });
              },
            });
            break;
          }
          case 'user': { // Same token of Django
            // Create and store the new channel
            const channel = socket.channel(channelId, { id: userId, token, role });
            channels[channelId] = channel;

            // Attatch the default events to the joineed channel
            attachChannelEvents({
              actionEvent,
              channel,
              channelId,
              userId,
              dispatch,
            });
            break;
          }
          case 'trip': {
            GetToken({
              resource: Trips.custom.getRoomToken,
              channelDetail,
              role: DEFAULT_SOCKET_ROLE,
              token,
              resolve: (data) => {
                // Create and store the new channel
                const channel = socket.channel(channelId, { id: userId, token: data.token, role });
                channels[channelId] = channel;

                // Attatch the default events to the joineed channel
                attachChannelEvents({
                  actionEvent,
                  channel,
                  channelId,
                  userId,
                  dispatch,
                });
              },
            });
            break;
          }
          default: break;
        }
      }

      break;
    }
    case types.RPS_DISJOINED: {
      const { payload: { channelId } } = action;
      const channel = channels[channelId];
      if (typeof channel !== 'undefined') {
        channel.leave().receive(
          'ok',
          () => { next(action); },
        );

        // Stop middleware chain
        return {};
      }

      break;
    }
    case types.RPS_PUSHED: {
      const { payload: { channelId, eventName, data = {} } } = action;
      const channel = channels[channelId];
      if (typeof channel !== 'undefined' /* check socket is connected and channel is connected */) {
        channel.push(eventName, data);
      }

      break;
    }
    default: {
      return next(action);
    }
  }

  return next(action);
};

export default createPhoenixSocketMiddleware;



// WEBPACK FOOTER //
// ./src/scripts/lib/socketMiddleware.js